import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const SortDropdown = ({ selectedSort, setSelectedSort, sortOrders, className, hideIcon = false }) => {
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const sortDropdownRef = useRef(null);
  const itemRefs = useRef([]);
  const { t, i18n } = useTranslation("translation");

  const isRTL = i18n.language === "ar";
  const options = [
    { id: "recent", label: `${t("Recent")}` },
    { id: "oldest", label: `${t("Oldest")}` },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToItem = (index) => {
    const el = itemRefs.current[index];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest" });
    }
  };

  const handleKeyDown = (e) => {
    if (!showSortDropdown) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = (highlightedIndex + 1) % options.length;
      setHighlightedIndex(next);
      scrollToItem(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = (highlightedIndex - 1 + options.length) % options.length;
      setHighlightedIndex(prev);
      scrollToItem(prev);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < options.length) {
        const selected = options[highlightedIndex];
        setSelectedSort(selected.id);
        sortOrders(selected.id);
        setShowSortDropdown(false);
        setHighlightedIndex(-1);
      }
    }
  };

  return (
    <div
      className={`custom-sort-wrapper ${className || ""}`}
      ref={sortDropdownRef}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <label htmlFor="sort-dropdown-button" className="oh-field__label custom-sort-label">
        {!hideIcon && (
          <i className="bi bi-arrow-down-up oh-filter-bar__icon" aria-hidden="true" />
        )}
        {t("toolBar.sort")}
      </label>

      <button
        id="sort-dropdown-button"
        type="button"
        className="custom-sort-button"
        onClick={() => {
          setShowSortDropdown((prev) => {
            const next = !prev;
            if (next) setHighlightedIndex(0);
            else setHighlightedIndex(-1);
            return next;
          });
        }}
      >
        <span className="custom-filter-button-text">
          {options.find((o) => o.id === selectedSort)?.label || t("Recent")}
        </span>
        <i
          className={`bi bi-chevron-down toolbar-dropdown-icon ${
            showSortDropdown ? "toolbar-dropdown-icon-rotate" : ""
          }`}
        />
      </button>

      {showSortDropdown && (
        <div className="custom-sort-menu">
          {options.map((option, index) => (
            <div
              key={option.id}
              ref={(el) => (itemRefs.current[index] = el)}
              className={`custom-dropdown-item${
                selectedSort === option.id ? " active" : ""
              }${highlightedIndex === index ? " highlighted" : ""}`}
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseLeave={() => setHighlightedIndex(-1)}
              onClick={() => {
                setSelectedSort(option.id);
                sortOrders(option.id);
                setShowSortDropdown(false);
                setHighlightedIndex(-1);
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SortDropdown;
